import { useQuery } from '@tanstack/react-query';
import { useLayoutEffect, useMemo } from 'react';

import { arrayToMap } from '~/common/utils/arrayToMap';
import { STALE_TIME_5_MIN } from '~/domains/common/constants/query';

import { getPromotedPredictionMarkets } from '../services/market.service.api';
import { MarketQueryKeys, PROMOTED_MARKETS_PAGE_SIZE } from '../constants/query';
import { useEntitiesStore } from '../state/entities.store';
import { useNormalizedMarketsStore } from '../state/normalizedMarkets.store';
import type {
  GetPromotedMarketsParams,
  PredictionMarketsResponse,
} from '../services/interfaces/market.types';

// MarketCard draws a gameline matchup, so a promoted future_team_champion group would render an
// empty card. Filtered server-side to keep `total` honest.
export const PROMOTED_MARKETS_PARAMS: GetPromotedMarketsParams = {
  limit: PROMOTED_MARKETS_PAGE_SIZE,
  types: ['gameline'],
};

export const PROMOTED_MARKETS_KEY =
  MarketQueryKeys.PREDICTION_MARKETS_PROMOTED(PROMOTED_MARKETS_PARAMS);

/** Ids come off the response, not the store, because promotion order is the server's. */
export const usePromotedMarkets = ({ enabled }: { enabled: boolean }) => {
  const { data, isLoading } = useQuery<PredictionMarketsResponse, Error>({
    queryKey: PROMOTED_MARKETS_KEY,
    queryFn: () => getPromotedPredictionMarkets(PROMOTED_MARKETS_PARAMS),
    enabled,
    staleTime: STALE_TIME_5_MIN,
  });

  // Layout effect so a cached response hydrates the stores before paint: the query cache
  // outlives them, so a warm remount would otherwise render against an empty store.
  useLayoutEffect(() => {
    if (!data) return;

    const { events, teams, entities, sub_categories: subCategories } = data.included;
    useEntitiesStore.getState().mergeEntities(events, teams, entities, subCategories);
    useNormalizedMarketsStore.getState().appendBrowseData(data.data, arrayToMap(events));
  }, [data]);

  const marketGroupIds = useMemo(
    () => data?.data.map((marketGroup) => marketGroup.id) ?? [],
    [data]
  );

  return { marketGroupIds, isLoading };
};
